import React, { useRef, useState, useEffect } from "react";
import { Button } from "primereact/button";
import CameraUploader from "presentation/mobile/components/CameraUploader";
import { compressImage } from "presentation/mobile/utils/compress-image.util";

export interface MobileUploadedFile {
    file: File;
    name: string;
    url: string;
}

interface MobileDocumentUploaderProps {
    value: MobileUploadedFile | null;
    onChange: (doc: MobileUploadedFile | null) => void;
    accept?: string;
    label?: string;
    allowCamera?: boolean;
    onView?: (doc: MobileUploadedFile) => void;
}

const MobileDocumentUploader: React.FC<MobileDocumentUploaderProps> = ({
    value,
    onChange,
    accept = ".pdf,.jpg,.jpeg,.png",
    label,
    allowCamera = true,
    onView,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [showCamera, setShowCamera] = useState(false);
    const [pictureSrc, setPictureSrc] = useState<string | null>(null);
    const [isCompressing, setIsCompressing] = useState(false);

    // Processar foto capturada pela câmara
    useEffect(() => {
        if (!pictureSrc) return;

        setIsCompressing(true);
        fetch(pictureSrc)
            .then((res) => res.blob())
            .then(async (blob) => {
                try {
                    const compressedBlob = await compressImage(blob, {
                        mimeType: "image/jpeg",
                    });
                    const file = new File(
                        [compressedBlob],
                        `foto_${Date.now()}.jpg`,
                        { type: "image/jpeg" }
                    );
                    onChange({
                        file,
                        name: file.name,
                        url: URL.createObjectURL(file),
                    });
                } catch (err) {
                    console.error("[MobileDocumentUploader] Erro ao comprimir foto:", err);
                    const file = new File([blob], `foto_${Date.now()}.png`, {
                        type: "image/png",
                    });
                    onChange({
                        file,
                        name: file.name,
                        url: URL.createObjectURL(file),
                    });
                }
            })
            .catch((err) => {
                console.error("[MobileDocumentUploader] Erro ao obter blob da foto:", err);
            })
            .finally(() => {
                setIsCompressing(false);
                setPictureSrc(null);
            });
    }, [pictureSrc, onChange]);

    // Tratar seleção de ficheiro
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.type.startsWith("image/")) {
            try {
                setIsCompressing(true);
                const compressedBlob = await compressImage(file, {
                    mimeType: "image/jpeg",
                });
                const compressedFile = new File(
                    [compressedBlob],
                    file.name.replace(/\.[^/.]+$/, ".jpg"),
                    { type: "image/jpeg" }
                );
                onChange({
                    file: compressedFile,
                    name: compressedFile.name,
                    url: URL.createObjectURL(compressedFile),
                });
            } catch (err) {
                console.error("[MobileDocumentUploader] Erro ao comprimir ficheiro:", err);
                onChange({
                    file,
                    name: file.name,
                    url: URL.createObjectURL(file),
                });
            } finally {
                setIsCompressing(false);
            }
        } else {
            onChange({
                file,
                name: file.name,
                url: URL.createObjectURL(file),
            });
        }
    };

    const handleView = () => {
        if (!value) return;
        if (onView) {
            onView(value);
        } else {
            window.open(value.url, "_blank");
        }
    };

    return (
        <div className="w-full flex flex-column gap-2">
            {label && (
                <span className="text-xs font-semibold" style={{ color: "#475569" }}>
                    {label}
                </span>
            )}

            {isCompressing ? (
                <div className="card-mobile p-4 flex flex-column align-items-center justify-content-center gap-2">
                    <i className="pi pi-spin pi-spinner text-2xl" style={{ color : "#0084D1" }} />
                    <span className="text-xs font-medium" style={{ color: "#64748B" }}>
                        A processar imagem...
                    </span>
                </div>
            ) : value ? (
                /* Card do Ficheiro Selecionado */
                <div className="card-mobile p-3 flex flex-column gap-2">
                    <div className="flex align-items-center gap-2 overflow-hidden">
                        <i className="pi pi-file text-xl flex-shrink-0" style={{ color : "#0084D1" }} />
                        <span
                            className="font-medium text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis flex-1"
                            style={{ color: "#0F172A" }}
                            title={value.name}
                        >
                            {value.name}
                        </span>
                    </div>

                    <div className="flex gap-2 justify-content-end pt-1">
                        <Button
                            label="Ver"
                            icon="pi pi-eye"
                            severity="secondary"
                            outlined
                            size="small"
                            type="button"
                            className="btn-mobile text-xs py-1 px-3"
                            onClick={handleView}
                        />
                        <Button
                            label="Remover"
                            icon="pi pi-trash"
                            severity="danger"
                            outlined
                            size="small"
                            type="button"
                            className="btn-mobile text-xs py-1 px-3"
                            onClick={() => onChange(null)}
                        />
                    </div>
                </div>
            ) : (
                /* Action Cards Modernos */
                <div className="flex gap-2 w-full">
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept={accept}
                        onChange={handleFileChange}
                    />

                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex-1 flex align-items-center justify-content-center gap-2 py-2 px-3 border-1 cursor-pointer"
                        style={{
                            borderRadius: "8px",
                            borderColor: "#E2E8F0",
                            backgroundColor: "#F8FAFC",
                            minHeight: "42px",
                            transition: "all 0.2s ease",
                        }}
                    >
                        <i className="pi pi-folder-open text-base" style={{ color : "#0084D1" }} />
                        <span className="text-xs font-semibold" style={{ color: "#1E293B" }}>
                            Ficheiro
                        </span>
                    </button>

                    {allowCamera && (
                        <button
                            type="button"
                            onClick={() => setShowCamera(true)}
                            className="flex-1 flex align-items-center justify-content-center gap-2 py-2 px-3 border-1 cursor-pointer "
                            style={{
                                borderRadius: "8px",
                                borderColor: "#E2E8F0",
                                backgroundColor: "#F8FAFC",
                                minHeight: "42px",
                                transition: "all 0.2s ease",
                            }}
                        >
                            <i className="pi pi-camera text-base" style={{ color : "#0084D1" }} />
                            <span className="text-xs font-semibold" style={{ color: "#1E293B" }}>
                                Tirar Foto
                            </span>
                        </button>
                    )}
                </div>
            )}

            {allowCamera && (
                <CameraUploader
                    dialogVisible={showCamera}
                    setDialogVisible={setShowCamera}
                    setImageURL={setPictureSrc}
                />
            )}
        </div>
    );
};

export default MobileDocumentUploader;